// OAuth utility functions for generating random state and nonce values

/**
 * Generate a random state string for OAuth flows
 * @returns {string} Random hexadecimal string
 */
export function generateRandomState() {
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  return Array.from(array, (dec) => dec.toString(16)).join('');
}

/**
 * Generate a random nonce string for OAuth flows
 * @returns {string} Random hexadecimal string
 */
export function generateRandomNonce() {
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  return Array.from(array, (dec) => dec.toString(16)).join('');
}

/**
 * Decode a JWT payload without verifying the signature
 * @param {string} token - Raw JWT string
 * @returns {Object|null} Decoded claims, or null when the token is unparseable
 */
export function decodeJwtPayload(token) {
  if (typeof token !== 'string') {
    return null;
  }

  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/**
 * Check whether a JWT is expired (or too close to expiry to be worth sending)
 * @param {string} token - Raw JWT string
 * @param {number} [skewSeconds] - Treat a token expiring within this window as expired
 * @returns {boolean} true when the token is expired, undecodable, or has no exp claim
 */
export function isJwtExpired(token, skewSeconds = 60) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }

  return payload.exp - skewSeconds <= Math.floor(Date.now() / 1000);
}
